<template lang="pug">
    .floor-plan-canvas(
        ref="canvas"
        tabindex="0"
        :class="{ 'floor-plan-canvas--panning': isPanning }"
        @pointerdown.capture="focusCanvas"
        @pointerdown="onPanStart"
        @mousemove="onMouseMove"
        @mouseup="onMouseUp"
        @mouseleave="onMouseUp"
    )
        //- Room area, fills the whole canvas
        .floor-plan-canvas__room(ref="room")
            //- Grid background
            .floor-plan-canvas__grid(:style="gridStyle")

            //- Zoomable stage holding the table coordinate space
            .floor-plan-canvas__stage(ref="stage" :style="stageStyle")
                //- Objects like counters or dividers, rendered below the tables
                floor-plan-fixture(
                    v-for="fixture in fixtures"
                    :key="'fixture-' + fixture.id"
                    :fixture="withKeyboardMove('fixture', fixture)"
                    :selected="fixture.id === selectedFixtureId"
                    :edit-mode="editMode"
                    :zoom="zoom"
                    :target-room-name="roomNames[fixture.targetRoomId] || ''"
                    @select="$emit('select-fixture', $event)"
                    @change="$emit('change-fixture', $event)"
                    @open-properties="$emit('open-fixture-properties', $event)"
                    @navigate="$emit('navigate-room', $event)"
                )

                //- Table elements
                floor-plan-table(
                    v-for="table in tables"
                    :key="table.id"
                    :table="withKeyboardMove('table', table)"
                    :selected="table.id === selectedTableId"
                    :multi-selected="multiSelectedTableIds.indexOf(String(table.id)) !== -1"
                    :selection-mode-active="selectionModeActive"
                    :in-combination="combinationTableIds.indexOf(String(table.id)) !== -1"
                    :edit-mode="editMode"
                    :drop-active="dropActive"
                    :reservation-info="getReservationInfo(table.id)"
                    :dynamic-status="dynamicStatuses[table.id] || null"
                    @select="$emit('select-table', $event)"
                    @table-click="onTableClick"
                    @drag-start="onDragStart"
                    @reservation-drop="$emit('reservation-drop', $event)"
                )

                //- Drop indicator when dragging
                .floor-plan-canvas__drop-indicator(v-if="isDragging" :style="dropIndicatorStyle")

        //- Zoom controls
        .floor-plan-canvas__zoom-controls
            el-tooltip(:content="$t('Zoom out')" placement="top")
                el-button.floor-plan-canvas__zoom-button(
                    size="mini"
                    circle
                    icon="el-icon-minus"
                    :disabled="zoom <= minimumZoom"
                    @click="zoomOut"
                )
            span.floor-plan-canvas__zoom-value(@click="resetZoom") {{ zoomPercentage }}%
            el-tooltip(:content="$t('Zoom in')" placement="top")
                el-button.floor-plan-canvas__zoom-button(
                    size="mini"
                    circle
                    icon="el-icon-plus"
                    :disabled="zoom >= maximumZoom"
                    @click="zoomIn"
                )
            .floor-plan-canvas__zoom-divider
            el-tooltip(:content="$t('Fit to view')" placement="top")
                el-button.floor-plan-canvas__zoom-button(
                    size="mini"
                    circle
                    @click="fitToContent"
                )
                    i.el-icon-fa-compress-arrows-alt.fas
</template>

<i18n>
{
    "de": {
        "Zoom in": "Vergrößern",
        "Zoom out": "Verkleinern",
        "Fit to view": "Alles einpassen"
    }
}
</i18n>

<script>
import _ from 'lodash';
import FloorPlanFixture from './FloorPlanFixture.vue';
import FloorPlanTable from './FloorPlanTable.vue';

const MINIMUM_ZOOM = 0.3;
const MAXIMUM_ZOOM = 2;
const MAXIMUM_FIT_ZOOM = 1.5;
const ZOOM_STEP = 0.1;
const FIT_PADDING = 32;
const GRID_SIZE = 20;
const PAN_CLICK_THRESHOLD = 4;
const PAN_MINIMUM_VISIBLE = 80;
const KEYBOARD_STEP = 10;
const KEYBOARD_FINE_STEP = 1;
const KEYBOARD_SAVE_DELAY = 350;

export default {
    name: 'FloorPlanCanvas',
    components: {
        FloorPlanFixture,
        FloorPlanTable
    },
    props: {
        tables: {
            type: Array,
            default: function () { return []; }
        },
        fixtures: {
            type: Array,
            default: function () { return []; }
        },
        selectedFixtureId: {
            type: String,
            default: null
        },
        roomId: {
            type: String,
            default: null
        },
        rooms: {
            type: Array,
            default: function () { return []; }
        },
        selectedTableId: {
            type: String,
            default: null
        },
        multiSelectedTableIds: {
            type: Array,
            default: function () { return []; }
        },
        selectionModeActive: {
            type: Boolean,
            default: false
        },
        combinationTableIds: {
            type: Array,
            default: function () { return []; }
        },
        width: {
            type: Number,
            default: 800
        },
        height: {
            type: Number,
            default: 520
        },
        editMode: {
            type: Boolean,
            default: true
        },
        dropActive: {
            type: Boolean,
            default: false
        },
        tableReservations: {
            type: Object,
            default: function () { return {}; }
        },
        dynamicStatuses: {
            type: Object,
            default: function () { return {}; }
        }
    },
    data: function () {
        return {
            isDragging: false,
            dragTable: null,
            dragOffsetX: 0,
            dragOffsetY: 0,
            dragStartClientX: 0,
            dragStartClientY: 0,
            dragX: 0,
            dragY: 0,
            didDrag: false,
            suppressNextClick: false,
            zoom: 1,
            offsetX: 0,
            offsetY: 0,
            isPanning: false,
            panState: null,
            keyboardMove: null
        };
    },
    computed: {
        roomNames: function () {
            const names = {};

            this.rooms.forEach((room) => {
                names[room.id] = room.name;
            });

            return names;
        },
        currentSelection: function () {
            if (this.selectedFixtureId) {
                const fixture = this.fixtures.find((candidate) => {
                    return candidate.id === this.selectedFixtureId;
                });

                if (fixture) {
                    return { kind: 'fixture', element: fixture };
                }
            }

            if (this.selectedTableId) {
                const table = this.tables.find((candidate) => {
                    return candidate.id === this.selectedTableId;
                });

                if (table) {
                    return { kind: 'table', element: table };
                }
            }

            return null;
        },
        minimumZoom: function () {
            return MINIMUM_ZOOM;
        },
        maximumZoom: function () {
            return MAXIMUM_ZOOM;
        },
        zoomPercentage: function () {
            return Math.round(this.zoom * 100);
        },
        stageStyle: function () {
            // The stage keeps the table coordinate space; it is centered in the room and
            // scaled around its own center, so the offset stays in screen pixels.
            return {
                width: this.width + 'px',
                height: this.height + 'px',
                transform: 'translate(-50%, -50%) translate(' + this.offsetX + 'px, ' + this.offsetY + 'px) scale(' + this.zoom + ')'
            };
        },
        gridStyle: function () {
            const size = GRID_SIZE * this.zoom;
            return { backgroundSize: size + 'px ' + size + 'px' };
        },
        dropIndicatorStyle: function () {
            if (!this.dragTable) return {};
            return {
                left: this.dragX + 'px',
                top: this.dragY + 'px',
                width: this.dragTable.width + 'px',
                height: this.dragTable.height + 'px',
                borderRadius: this.dragTable.shape === 'round' ? '50%' : '6px'
            };
        }
    },
    watch: {
        roomId: function () {
            this.saveKeyboardMoveNow();
            this.$nextTick(() => {
                this.fitToContent();
            });
        },
        selectedFixtureId: function () {
            this.saveKeyboardMoveNow();
        },
        selectedTableId: function () {
            this.saveKeyboardMoveNow();
        },
        editMode: function () {
            this.saveKeyboardMoveNow();
        },
        tables: function (newTables, oldTables) {
            // Fit once as soon as the tables of the initially selected room arrive
            if (!oldTables.length && newTables.length) {
                this.$nextTick(() => {
                    this.fitToContent();
                });
            }
        }
    },
    created: function () {
        this.saveKeyboardMove = _.debounce(this.saveKeyboardMoveNow, KEYBOARD_SAVE_DELAY);
    },
    mounted: function () {
        window.addEventListener('keydown', this.onKeyDown);
        this.$nextTick(() => {
            this.fitToContent();
        });
    },
    beforeDestroy: function () {
        window.removeEventListener('keydown', this.onKeyDown);
        this.saveKeyboardMoveNow();
        this.onPanEnd();
    },
    methods: {
        withKeyboardMove: function (kind, element) {
            const move = this.keyboardMove;

            if (!move || move.kind !== kind || move.id !== element.id) {
                return element;
            }

            return _.assign({}, element, { x: move.x, y: move.y });
        },
        focusCanvas: function () {
            const canvas = this.$refs.canvas;

            if (canvas && document.activeElement !== canvas) {
                canvas.focus({ preventScroll: true });
            }
        },
        onKeyDown: function (event) {
            if (!this.editMode || this.selectionModeActive || !this.currentSelection) {
                return;
            }

            // Only keystrokes that do not belong to another widget count
            if (!this.isPlainKeyboardTarget(event.target)) {
                return;
            }

            const step = event.shiftKey ? KEYBOARD_FINE_STEP : KEYBOARD_STEP;

            switch (event.key) {
                case 'ArrowLeft':
                    this.nudgeSelection(-step, 0, event);
                    break;
                case 'ArrowRight':
                    this.nudgeSelection(step, 0, event);
                    break;
                case 'ArrowUp':
                    this.nudgeSelection(0, -step, event);
                    break;
                case 'ArrowDown':
                    this.nudgeSelection(0, step, event);
                    break;
                case 'Delete':
                case 'Backspace':
                    this.deleteSelection(event);
                    break;
                default:
                    break;
            }
        },
        isPlainKeyboardTarget: function (target) {
            if (!target || target === document.body || target === this.$el) {
                return true;
            }

            if (this.$el.contains(target) || !target.closest) {
                return true;
            }

            // Dialogs, drawers and form controls keep their own arrow key handling
            const isFormControl = ['INPUT', 'TEXTAREA', 'SELECT'].indexOf(target.tagName) !== -1
                || target.isContentEditable;

            return !isFormControl && !target.closest('.el-dialog, .el-drawer, [tabindex]');
        },
        nudgeSelection: function (deltaX, deltaY, event) {
            const selection = this.currentSelection;

            event.preventDefault();

            const move = this.keyboardMove;
            const base = move && move.kind === selection.kind && move.id === selection.element.id
                ? move
                : selection.element;

            this.keyboardMove = {
                kind: selection.kind,
                id: selection.element.id,
                x: base.x + deltaX,
                y: base.y + deltaY
            };

            this.saveKeyboardMove();
        },
        saveKeyboardMoveNow: function () {
            const move = this.keyboardMove;

            this.saveKeyboardMove.cancel();

            if (!move) {
                return;
            }

            this.keyboardMove = null;

            if (move.kind === 'fixture') {
                this.$emit('change-fixture', { id: move.id, geometry: { x: move.x, y: move.y } });
                return;
            }

            this.$emit('move-table', { id: move.id, x: move.x, y: move.y });
        },
        deleteSelection: function (event) {
            const selection = this.currentSelection;

            event.preventDefault();
            this.saveKeyboardMove.cancel();
            this.keyboardMove = null;

            this.$emit(selection.kind === 'fixture' ? 'delete-fixture' : 'delete-table', selection.element.id);
        },
        getReservationInfo: function (tableId) {
            return this.tableReservations[tableId] || null;
        },
        clampZoom: function (zoom) {
            return Math.min(MAXIMUM_ZOOM, Math.max(MINIMUM_ZOOM, zoom));
        },
        zoomIn: function () {
            this.zoom = this.clampZoom(this.zoom + ZOOM_STEP);
            this.clampOffsets();
        },
        zoomOut: function () {
            this.zoom = this.clampZoom(this.zoom - ZOOM_STEP);
            this.clampOffsets();
        },
        resetZoom: function () {
            this.zoom = 1;
            this.offsetX = 0;
            this.offsetY = 0;
        },
        contentBounds: function () {
            const elements = this.tables.concat(this.fixtures);

            if (!elements.length) {
                return null;
            }

            let left = Infinity;
            let top = Infinity;
            let right = -Infinity;
            let bottom = -Infinity;

            elements.forEach((element) => {
                left = Math.min(left, element.x);
                top = Math.min(top, element.y);
                right = Math.max(right, element.x + element.width);
                bottom = Math.max(bottom, element.y + element.height);
            });

            return { left: left, top: top, right: right, bottom: bottom };
        },
        fitToContent: function () {
            const canvas = this.$refs.canvas;
            const bounds = this.contentBounds();

            if (!canvas || !bounds) {
                this.resetZoom();
                return;
            }

            const availableWidth = Math.max(canvas.clientWidth - FIT_PADDING, 1);
            const availableHeight = Math.max(canvas.clientHeight - FIT_PADDING, 1);
            const contentWidth = Math.max(bounds.right - bounds.left, 1);
            const contentHeight = Math.max(bounds.bottom - bounds.top, 1);

            const zoom = Math.min(
                MAXIMUM_FIT_ZOOM,
                Math.max(MINIMUM_ZOOM, Math.min(availableWidth / contentWidth, availableHeight / contentHeight))
            );

            // Move the content center onto the canvas center — the offset is applied unscaled.
            const contentCenterX = (bounds.left + bounds.right) / 2;
            const contentCenterY = (bounds.top + bounds.bottom) / 2;

            this.zoom = zoom;
            this.offsetX = -zoom * (contentCenterX - this.width / 2);
            this.offsetY = -zoom * (contentCenterY - this.height / 2);
        },
        // Half the span the plan occupies around the stage center — elements placed
        // outside the room stretch it, so panning can always reach them
        contentReach: function () {
            const bounds = this.contentBounds();
            const centerX = this.width / 2;
            const centerY = this.height / 2;

            if (!bounds) {
                return { x: centerX, y: centerY };
            }

            return {
                x: Math.max(centerX, centerX - bounds.left, bounds.right - centerX),
                y: Math.max(centerY, centerY - bounds.top, bounds.bottom - centerY)
            };
        },
        clampOffsets: function () {
            const canvas = this.$refs.canvas;

            if (!canvas) {
                return;
            }

            // Keep at least a strip of the plan inside the visible area
            const reach = this.contentReach();
            const maximumOffsetX = Math.max(reach.x * this.zoom + canvas.clientWidth / 2 - PAN_MINIMUM_VISIBLE, 0);
            const maximumOffsetY = Math.max(reach.y * this.zoom + canvas.clientHeight / 2 - PAN_MINIMUM_VISIBLE, 0);

            this.offsetX = Math.min(maximumOffsetX, Math.max(-maximumOffsetX, this.offsetX));
            this.offsetY = Math.min(maximumOffsetY, Math.max(-maximumOffsetY, this.offsetY));
        },
        isEmptyCanvasArea: function (element) {
            return element === this.$refs.canvas || element === this.$refs.room || element === this.$refs.stage;
        },
        onPanStart: function (event) {
            // Panning starts on empty areas only — tables keep their own drag behaviour
            if (this.panState || this.isDragging || !this.isEmptyCanvasArea(event.target)) {
                return;
            }

            // Suppresses text selection and page scrolling while wiping across the plan
            event.preventDefault();

            const canvas = this.$refs.canvas;

            if (canvas.setPointerCapture && event.pointerId !== undefined) {
                canvas.setPointerCapture(event.pointerId);
            }

            this.panState = {
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                startOffsetX: this.offsetX,
                startOffsetY: this.offsetY,
                moved: false
            };
            this.isPanning = true;

            window.addEventListener('pointermove', this.onPanMove);
            window.addEventListener('pointerup', this.onPanEnd);
            window.addEventListener('pointercancel', this.onPanEnd);
        },
        onPanMove: function (event) {
            if (!this.panState || event.pointerId !== this.panState.pointerId) {
                return;
            }

            const deltaX = event.clientX - this.panState.startX;
            const deltaY = event.clientY - this.panState.startY;

            if (Math.abs(deltaX) > PAN_CLICK_THRESHOLD || Math.abs(deltaY) > PAN_CLICK_THRESHOLD) {
                this.panState.moved = true;
            }

            this.offsetX = this.panState.startOffsetX + deltaX;
            this.offsetY = this.panState.startOffsetY + deltaY;
            this.clampOffsets();
        },
        onPanEnd: function (event) {
            if (!this.panState || (event && event.pointerId !== this.panState.pointerId)) {
                return;
            }

            const canvas = this.$refs.canvas;
            const pointerId = this.panState.pointerId;

            if (canvas && canvas.releasePointerCapture && pointerId !== undefined && canvas.hasPointerCapture(pointerId)) {
                canvas.releasePointerCapture(pointerId);
            }

            window.removeEventListener('pointermove', this.onPanMove);
            window.removeEventListener('pointerup', this.onPanEnd);
            window.removeEventListener('pointercancel', this.onPanEnd);

            // A tap without movement on an empty area clears the current selection
            if (!this.panState.moved) {
                this.$emit('select-table', { id: null, additive: false });
                this.$emit('select-fixture', null);
            }

            this.panState = null;
            this.isPanning = false;
        },
        onDragStart: function (payload) {
            if (!this.editMode) return;
            const table = payload.table;
            const event = payload.event;
            const stageRect = this.$refs.stage.getBoundingClientRect();

            this.isDragging = true;
            this.dragTable = table;
            this.dragOffsetX = (event.clientX - stageRect.left) / this.zoom - table.x;
            this.dragOffsetY = (event.clientY - stageRect.top) / this.zoom - table.y;
            this.dragStartClientX = event.clientX;
            this.dragStartClientY = event.clientY;
            this.dragX = table.x;
            this.dragY = table.y;
            this.didDrag = false;

            this.$emit('select-table', { id: table.id, additive: false });
        },
        onMouseMove: function (event) {
            if (!this.isDragging || !this.dragTable) return;
            const movedX = Math.abs(event.clientX - this.dragStartClientX);
            const movedY = Math.abs(event.clientY - this.dragStartClientY);
            if (movedX > 4 || movedY > 4) {
                this.didDrag = true;
            }
            const stageRect = this.$refs.stage.getBoundingClientRect();
            let newX = (event.clientX - stageRect.left) / this.zoom - this.dragOffsetX;
            let newY = (event.clientY - stageRect.top) / this.zoom - this.dragOffsetY;

            // No room walls — tables are placed freely, also outside the room bounds

            // Snap to grid (10px)
            newX = Math.round(newX / 10) * 10;
            newY = Math.round(newY / 10) * 10;

            this.dragX = newX;
            this.dragY = newY;
        },
        onMouseUp: function () {
            if (this.isDragging && this.dragTable) {
                if (this.didDrag) {
                    this.$emit('move-table', {
                        id: this.dragTable.id,
                        x: this.dragX,
                        y: this.dragY
                    });
                }
                this.suppressNextClick = this.didDrag;
            }
            this.isDragging = false;
            this.dragTable = null;
            this.didDrag = false;
        },
        onTableClick: function (payload) {
            if (this.suppressNextClick) {
                this.suppressNextClick = false;
                return;
            }
            // payload is { id, additive }
            if (payload && payload.additive) {
                this.$emit('select-table', payload);
                return;
            }
            const tableId = payload && payload.id;
            this.$emit('open-table-details', tableId);
        }
    }
};
</script>

<style lang="sass" scoped>
@import '~@/assets/sass/variables'

.floor-plan-canvas
    position: relative
    width: 100%
    height: 100%
    // Fallback for the vertical layout, where the panel has no definite height
    min-height: 420px
    flex: 1 1 auto
    background: #fff
    border: 1px solid #dcdfe6
    border-radius: 4px
    overflow: hidden
    cursor: grab
    // Wiping across the plan pans it instead of scrolling the page
    touch-action: none
    // Focusable for the keyboard shortcuts, but without a focus ring around the plan
    outline: none

    &--panning
        cursor: grabbing
        user-select: none

    &__room
        position: absolute
        top: 0
        left: 0
        width: 100%
        height: 100%

    &__stage
        position: absolute
        top: 50%
        left: 50%
        transform-origin: center center

    &__zoom-controls
        position: absolute
        right: 12px
        bottom: 12px
        cursor: default
        z-index: 20
        display: flex
        align-items: center
        gap: 6px
        padding: 4px 8px
        border-radius: 20px
        background: rgba(255, 255, 255, 0.95)
        border: 1px solid #e4e7ed
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08)

    &__zoom-value
        font-size: 0.75rem
        color: #909399
        min-width: 34px
        text-align: center
        cursor: pointer
        user-select: none

        &:hover
            color: $--color-primary

    &__zoom-divider
        width: 1px
        height: 16px
        background: #dcdfe6

    @media (pointer: coarse)
        &__zoom-controls
            gap: 10px
            padding: 6px 10px

        &__zoom-button
            padding: 9px

    &__grid
        position: absolute
        top: 0
        left: 0
        right: 0
        bottom: 0
        background-image: radial-gradient(circle, #dcdfe6 1px, transparent 1px)
        background-size: 20px 20px
        pointer-events: none

    &__drop-indicator
        position: absolute
        border: 2px dashed $--color-primary
        background: rgba($--color-primary, 0.1)
        pointer-events: none
        z-index: 10
</style>
